import { type FC, useState, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import serverVars from 'server-vars';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { Input } from 'dibs-elements/exports/Input';
import { ErrorMessage } from 'dibs-elements/exports/ErrorMessage';
import { ConnectMetaMaskButton } from './ConnectMetaMaskButton';
import { ConnectWalletConnectButton } from './ConnectWalletConnectButton';
import { useWeb3Connection } from '../useWeb3Connection';
import { type EthereumConnectionZipCodeForm_viewer$key } from './__generated__/EthereumConnectionZipCodeForm_viewer.graphql';

import styles from './EthereumConnectionZipCodeForm.scss';

const messages = defineMessages({
    title: {
        id: 'ethereum.zipCodeForm.title',
        defaultMessage: 'Zip Code',
    },
    internationalCustomer: {
        id: 'ethereum.zipCodeForm.internationalCustomer',
        defaultMessage: "I'm an international customer",
    },
    inputPlaceholder: {
        id: 'ethereum.zipCodeForm.inputPlaceholder',
        defaultMessage: 'Zip Code',
    },
    buttonLabel: {
        id: 'ethereum.zipCodeForm.buttonLabel',
        defaultMessage: 'Continue',
    },
    invalidZipCode: {
        id: 'ethereum.zipCodeForm.invalidZipCode',
        defaultMessage: 'Please provide valid US zip code',
    },
});

const isValidZipCode = (input: string): boolean => {
    //five-digit and nine-digit (ZIP+4): https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s14.html
    return /^[0-9]{5}(?:-[0-9]{4})?$/.test(input);
};

export type ZipCodeOutput = {
    isInternationalCustomer: boolean;
    zipCode: string;
    walletType?: string;
};

type Props = {
    onClick: (output: ZipCodeOutput) => void;
    viewer: EthereumConnectionZipCodeForm_viewer$key;
    initialZipCodeValue?: string;
    showWalletButtons?: boolean;
    isLoading?: boolean;
    errorMessage?: ReactNode;
    buttonLabel?: ReactNode;
};

export const EthereumConnectionZipCodeForm: FC<Props> = ({
    onClick,
    viewer: viewerRef,
    initialZipCodeValue = '',
    showWalletButtons,
    isLoading,
    errorMessage,
    buttonLabel,
}) => {
    const isMobile = serverVars.get('settings.isMobile');

    const intl = useIntl();
    const { connected, isMetaMaskInstalled } = useWeb3Connection();

    const [zipCode, setZipCode] = useState(
        initialZipCodeValue === 'INTL' ? '' : initialZipCodeValue
    );
    const [isInternationalCustomer, setIsInternationalCustomer] = useState(
        initialZipCodeValue === 'INTL'
    );
    const [showValidationError, setShowValidationError] = useState(false);

    const viewer = useFragment(
        graphql`
            fragment EthereumConnectionZipCodeForm_viewer on Viewer {
                walletConnectFeatureFlag: featureFlag(feature: "allowWalletConnect")
            }
        `,
        viewerRef
    );
    const { walletConnectFeatureFlag } = viewer || false;

    const handleButtonClick = (walletType: string = 'metamask'): void => {
        const isValid = isInternationalCustomer || isValidZipCode(zipCode);
        setShowValidationError(!isValid);
        if (!isValid) {
            return;
        }

        setShowValidationError(!isInternationalCustomer && !isValidZipCode(zipCode));

        onClick({
            isInternationalCustomer,
            zipCode,
            walletType,
        });
    };

    const valueHasNotChanged = (): boolean => {
        if (!initialZipCodeValue) {
            return false;
        }

        return (
            (initialZipCodeValue === 'INTL' && isInternationalCustomer) ||
            (initialZipCodeValue === zipCode && !isInternationalCustomer)
        );
    };

    const isButtonDisabled =
        (!isInternationalCustomer && !zipCode) || isLoading || valueHasNotChanged();

    return (
        <>
            <Checkbox
                dataTn="international-customer-checkbox"
                name="international-customer"
                checked={isInternationalCustomer}
                onChange={newValue => setIsInternationalCustomer(newValue)}
                label={intl.formatMessage(messages.internationalCustomer)}
            />

            {isInternationalCustomer ? null : (
                <div className={styles.inputContainer}>
                    <Input
                        value={zipCode}
                        onChange={e => setZipCode(e.target.value)}
                        dataTn="zip-code-input"
                        errorDataTn="input-error-data-tn"
                        placeholder={intl.formatMessage(messages.inputPlaceholder)}
                    />
                </div>
            )}
            {errorMessage ? <ErrorMessage dataTn="error-message" message={errorMessage} /> : null}
            {showValidationError ? (
                <ErrorMessage
                    dataTn="validation-error-message"
                    message={intl.formatMessage(messages.invalidZipCode)}
                />
            ) : null}
            <div className={styles.buttonContainer}>
                {showWalletButtons ? (
                    <div className={styles.connectButtons}>
                        {!(isMobile && !isMetaMaskInstalled) && (
                            <ConnectMetaMaskButton
                                fullWidth
                                disabled={isButtonDisabled}
                                connected={connected}
                                onConnect={() => handleButtonClick('metamask')}
                                metaMaskNotInstalled={!isMetaMaskInstalled}
                            />
                        )}
                        {walletConnectFeatureFlag && (
                            <ConnectWalletConnectButton
                                fullWidth
                                disabled={isButtonDisabled}
                                connected={connected}
                                onConnect={() => handleButtonClick('walletconnect')}
                            />
                        )}
                    </div>
                ) : (
                    <Button
                        dataTn="submit-zip-code-cta"
                        onClick={() => handleButtonClick()}
                        disabled={isButtonDisabled}
                        size="large"
                        type="primary"
                        fullWidth
                    >
                        <div className={styles.buttonContent}>
                            {buttonLabel ?? intl.formatMessage(messages.buttonLabel)}
                            {isLoading && (
                                <Spinner
                                    size="tiny"
                                    black
                                    containerClass={styles.spinnerContainer}
                                />
                            )}
                        </div>
                    </Button>
                )}
            </div>
        </>
    );
};
